import { useQuery } from "@tanstack/react-query";
import {
  Send,
  CheckCircle,
  ClipboardList,
  Plug,
  FileText,
  Users,
  HardDrive,
  ArrowRight,
  TrendingUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usageApi, billingApi } from "@/services/api";
import { capitaliseFirst } from "@/utils/transform";

// Helper function to format storage limit for display (input is in MB)
const formatStorageLimit = (valueInMB) => {
  if (!valueInMB || valueInMB <= 0 || valueInMB === -1) return "Unlimited";

  // If 1 GB or more, show in GB
  if (valueInMB >= 1024) {
    const gb = valueInMB / 1024;
    if (Number.isInteger(gb)) {
      return `${gb} GB`;
    }
    return `${gb.toFixed(1)} GB`;
  }

  // Show in MB
  return `${Math.round(valueInMB)} MB`;
};

// Usage stat card component
const UsageStatCard = ({ title, used, limit, icon: Icon, description }) => {
  const { t } = useTranslation();
  const percentage = limit === -1 ? 0 : Math.min((used / limit) * 100, 100);
  const isUnlimited = limit === -1;

  const getProgressColor = () => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-yellow-500";
    return "bg-primary";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {used.toLocaleString()}
          <span className="text-sm font-normal text-muted-foreground ml-1">
            / {isUnlimited ? "∞" : limit.toLocaleString()}
          </span>
        </div>
        {!isUnlimited && (
          <div className="mt-3">
            <Progress
              value={percentage}
              className={cn("h-2", getProgressColor())}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {percentage.toFixed(0)}% used
            </p>
          </div>
        )}
        {isUnlimited && (
          <p className="text-xs text-muted-foreground mt-1">
            {t("common.unlimited")}
          </p>
        )}
        {description && (
          <p className="text-xs text-muted-foreground mt-2">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

// Activity item component
const ActivityItem = ({ activity }) => {
  const getIcon = () => {
    switch (activity.type) {
      case "envelope_sent":
        return <Send className="h-4 w-4" aria-hidden="true" />;
      case "envelope_completed":
        return <CheckCircle className="h-4 w-4" aria-hidden="true" />;
      case "template_created":
        return <ClipboardList className="h-4 w-4" aria-hidden="true" />;
      case "api_call":
        return <Plug className="h-4 w-4" aria-hidden="true" />;
      default:
        return <FileText className="h-4 w-4" aria-hidden="true" />;
    }
  };

  const getLabel = () => {
    switch (activity.type) {
      case "envelope_sent":
        return "Envelope sent";
      case "envelope_completed":
        return "Envelope completed";
      case "template_created":
        return "Template created";
      case "api_call":
        return "API call";
      default:
        return activity.type;
    }
  };

  return (
    <div className="flex items-center gap-3 p-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">
          {activity.description || getLabel()}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(activity.date || activity.createdAt).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

// Monthly usage chart (simple bar representation)
const UsageChart = ({ history }) => {
  const { t } = useTranslation();

  // Ensure we have data and calculate max value
  if (!history || history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-muted-foreground">{t("usage.noHistory")}</p>
      </div>
    );
  }

  const maxValue = Math.max(...history.map((h) => h.envelopes || 0), 1);

  return (
    <div className="space-y-4">
      {/* Bars */}
      <div className="flex items-end justify-between gap-3 h-48 px-2">
        {history.map((month, index) => {
          const value = month.envelopes || 0;
          const heightPercent =
            maxValue > 0 ? Math.max((value / maxValue) * 100, 5) : 5;
          const barHeight = Math.max((heightPercent / 100) * 160, 8); // 160px max height

          return (
            <div
              key={month.month || `month-${index}`}
              className="flex-1 flex flex-col items-center justify-end gap-1"
            >
              <span className="text-xs font-medium text-foreground">
                {value}
              </span>
              <div
                className="w-full bg-primary rounded-t transition-all duration-300 min-w-6"
                style={{ height: `${barHeight}px` }}
              />
              <span className="text-xs text-muted-foreground font-medium mt-1">
                {month.month || `M${index + 1}`}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
        <div className="w-3 h-3 bg-primary rounded" aria-hidden="true" />
        <span>{t("usage.envelopesSent")}</span>
      </div>
    </div>
  );
};

// Loading skeleton
const UsageSkeleton = () => (
  <main className="space-y-6">
    <div className="space-y-2">
      <h1 className="sr-only">Loading usage data</h1>
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
    </div>
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-32" />
      ))}
    </div>
    <div className="grid gap-6 lg:grid-cols-2">
      <Skeleton className="h-64" />
      <Skeleton className="h-64" />
    </div>
  </main>
);

export const Usage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ============================================
  // TanStack Queries
  // ============================================

  // Query: Get current usage data
  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ["usage"],
    queryFn: async () => {
      const res = await usageApi.getUsage();
      return res.data?.data || res.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

  // Query: Get subscription/plan info (same source as Sidebar)
  const { data: subscription } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      const res = await billingApi.getSubscription();
      return res.data?.data || res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query: Get usage history (6 months)
  const { data: history = [] } = useQuery({
    queryKey: ["usage-history", 6],
    queryFn: async () => {
      const res = await usageApi.getHistory(6);
      const data = res.data?.data || res.data || [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query: Get recent activity
  const { data: activity = [] } = useQuery({
    queryKey: ["usage-activity", 10],
    queryFn: async () => {
      const res = await usageApi.getRecentActivity(10);
      const data = res.data?.data || res.data || [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30 * 1000, // 30 seconds
  });

  // ============================================
  // Loading State
  // ============================================

  if (usageLoading || !usage) {
    return <UsageSkeleton />;
  }

  // ============================================
  // Main Render
  // ============================================

  const { currentPeriod } = usage;

  // Get plan name from subscription (primary) or usage response (fallback)
  // subscription.plan is the plan name string directly
  const planName =
    subscription?.plan || usage?.plan?.name || usage?.plan || "Free";

  // Get limits from subscription (primary) or usage response (fallback)
  // Subscription has the correct limits for the current plan
  const limits = subscription?.limits || usage?.limits || {};

  return (
    <main className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("usage.title")}
          </h1>
          <p className="text-muted-foreground">{t("usage.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {capitaliseFirst(planName)} Plan
          </Badge>
          <Button
            variant="outline"
            onClick={() => navigate("/settings/billing")}
          >
            {planName === "enterprise" ? "Manage Plan" : "Upgrade Plan"}
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <UsageStatCard
          title={t("usage.envelopes", "Envelopes")}
          used={currentPeriod?.envelopes || 0}
          limit={limits?.envelopesPerMonth || 10}
          icon={Send}
          description={t("usage.thisBillingPeriod", "This billing period")}
        />
        <UsageStatCard
          title={t("usage.templates", "Templates")}
          used={currentPeriod?.templates || 0}
          limit={limits?.templates || 5}
          icon={ClipboardList}
        />
        <UsageStatCard
          title={t("usage.teamMembers", "Team Members")}
          used={currentPeriod?.teamMembers || 1}
          limit={limits?.teamMembers || 1}
          icon={Users}
        />
        <UsageStatCard
          title={t("usage.apiCalls", "API Calls")}
          used={currentPeriod?.apiCalls || 0}
          limit={limits?.apiCallsPerMonth || 100}
          icon={Plug}
          description={t("usage.thisBillingPeriod", "This billing period")}
        />
      </div>

      {/* Charts and Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Usage History Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
              {t("usage.usageHistory")}
            </CardTitle>
            <CardDescription>{t("usage.usageHistoryDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <UsageChart history={history} />
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t("usage.recentActivity")}</CardTitle>
              <CardDescription>{t("usage.recentActivityDesc")}</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/activity")}
            >
              {t("common.seeAll")}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {activity.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-muted-foreground">
                  {t("usage.noRecentActivity")}
                </p>
              </div>
            ) : (
              <div className="divide-y max-h-64 overflow-auto">
                {activity.map((item) => (
                  <ActivityItem
                    key={item.id || item.createdAt}
                    activity={item}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Storage Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" aria-hidden="true" />
            {t("usage.storageUsage")}
          </CardTitle>
          <CardDescription>{t("usage.storageUsageDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {((currentPeriod?.storageUsed || 0) / 1024 / 1024).toFixed(2)}{" "}
                  MB {t("common.used")}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t("common.of")} {formatStorageLimit(limits?.storageMB)}
                </span>
              </div>
              {limits?.storageMB !== -1 && (
                <Progress
                  value={
                    ((currentPeriod?.storageUsed || 0) /
                      1024 /
                      1024 /
                      (limits?.storageMB || 1)) *
                    100
                  }
                  className="h-3"
                />
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-3 pt-4">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold">
                  {currentPeriod?.documents || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("common.documents")}
                </p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold">
                  {currentPeriod?.envelopesTotal || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("admin.totalEnvelopes")}
                </p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold">
                  {currentPeriod?.completedEnvelopes || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("common.completed")}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </main>
  );
};

export default Usage;
